import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import EventRoundedIcon from '@mui/icons-material/EventRounded'
import { toast } from 'react-toastify'
import { updateTask } from 'services/taskService'

const toDateInput = value => {
  if (!value) return ''
  const s = String(value)
  return s.length >= 10 ? s.slice(0, 10) : s
}

const TaskDeadlineQuickEdit = ({ task, onSaved }) => {
  const [open, setOpen] = useState(false)
  const [value, setValue] = useState(toDateInput(task?.deadline))
  const [busy, setBusy] = useState(false)
  const [coords, setCoords] = useState(null)
  const btnRef = useRef(null)
  const popRef = useRef(null)

  useEffect(() => { setValue(toDateInput(task?.deadline)) }, [task?.deadline])

  useEffect(() => {
    if (!open) return
    const onClick = e => {
      if (
        popRef.current && !popRef.current.contains(e.target) &&
        btnRef.current && !btnRef.current.contains(e.target)
      ) setOpen(false)
    }
    const onKey = e => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  useEffect(() => {
    if (open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect()
      const margin = 12
      const width = 250
      const left = Math.max(margin, Math.min(r.left, window.innerWidth - width - margin))
      setCoords({ top: r.bottom + 6, left })
    }
  }, [open])

  const save = async () => {
    if (!value) { toast.info('Pick a date first.'); return }
    if (value === toDateInput(task?.deadline)) { setOpen(false); return }
    setBusy(true)
    try {
      await updateTask({ id: task.id, deadline: value })
      toast.success('Due date updated.')
      setOpen(false)
      onSaved?.()
    } catch (e) {
      toast.error('Could not update the due date. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  if (!task?.id) return null

  return (
    <>
      <IconBtn
        ref={btnRef}
        type='button'
        title='Change the due date'
        aria-label='Change the due date'
        onClick={() => setOpen(o => !o)}
      >
        <EventRoundedIcon style={{ fontSize: 15 }} />
      </IconBtn>

      {open && coords && createPortal(
        <Pop ref={popRef} role='dialog' aria-label='Change the due date' style={{ top: coords.top, left: coords.left }}>
          <PopLabel>Change due date</PopLabel>
          <DateInput
            type='date'
            value={value}
            autoFocus
            onChange={e => setValue(e.target.value)}
          />
          <PopActions>
            <CancelBtn type='button' onClick={() => setOpen(false)}>Cancel</CancelBtn>
            <SaveBtn type='button' disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save'}</SaveBtn>
          </PopActions>
        </Pop>,
        document.body
      )}
    </>
  )
}

export default TaskDeadlineQuickEdit

const IconBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  padding: 0;
  border: 1px solid ${({ theme }) => theme.colors.grey[500]};
  border-radius: 8px;
  background: #fff;
  color: ${({ theme }) => theme.colors.grey[800]};
  cursor: pointer;
  transition: border-color 160ms ease, color 160ms ease;

  &:hover { border-color: ${({ theme }) => theme.colors.secondary}; color: ${({ theme }) => theme.colors.secondary}; }
`

const Pop = styled.div`
  position: fixed;
  z-index: 100010;
  width: 250px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px;
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 12px;
  box-shadow: 0 18px 44px -22px rgba(12, 18, 41, 0.4);
`

const PopLabel = styled.span`
  font-size: 10px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const DateInput = styled.input`
  padding: 8px 11px;
  border: 1px solid ${({ theme }) => theme.colors.grey[500]};
  border-radius: 8px;
  font: inherit;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.haiti};

  &:focus { outline: none; border-color: ${({ theme }) => theme.colors.primary}; }
`

const PopActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`

const CancelBtn = styled.button`
  padding: 7px 13px;
  border: 1px solid ${({ theme }) => theme.colors.grey[500]};
  border-radius: 999px;
  background: #fff;
  color: ${({ theme }) => theme.colors.grey[800]};
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
`

const SaveBtn = styled.button`
  padding: 7px 15px;
  border: none;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;

  &:disabled { opacity: 0.6; cursor: default; }
`
